import type { TPropostaSimulacao, TRotinaArquivo } from '@/features/proposta/services/get-proposta-duplicar.service'
import type { TTipoProdutoValidoEnum } from '@/types/produto'
import type {
  TAssinaturaContratoEnum,
  TFormaPagamentoComissaoEnum,
  TOpcaoVagaEnum,
  TStatusProposta,
  TTipoAssinaturaEnum,
  TTipoDoCompradorEnum,
  TTipoDoVinculoDoCompradorEnum,
  TTipoEmolumentoEnum,
} from '@/types/proposta'
import type { TTipoAmortizacaoEnum, TFormaDePagamentoEnum } from '@/types/simulacao'

type TGrupoParcelas = { parcelas: Array<{ data: string; valor: number }> }

export type TVagasSelecionadas = Array<{
  nomeVaga: string
  id: number
  descricao: string
  substituiVagaPadrao?: boolean
}>

export type TVagaPadraoUnidadesVinculadasProps = {
  acessoriosVinculados: Array<{
    nome: string
    acessorios: Array<{ idExterno: number; nome: string }>
  }> | null
}

export type TAcessorioDaUnidade = {
  nome: string
  idExterno: number
  descricao: string
  acessorios: Array<{
    nome: string
    idExterno: number
    descricao: string
    substituiVagaPadrao: boolean
  }>
}

export type TGetVendedoresUnidadeNegocioResponse = Array<{
  id: string
  nome: string
  idExterno: number
  email: string
  possuiDadosDeComissao: boolean
}>

export type TPropostaLocationState = {
  simulacoesIds: string[]
}

export type TGetCorretagensResponse = Array<{
  idExterno: string
  percentualCorretagem: number | null
  valorCorretagem: number | null
}>

type TImobiliariaCorretor = {
  descricao: string
  idExterno: string
  documento: string
  tipoImobiliariaCorretor: 'Imobiliaria' | 'CorretorAutonomo'
  nome: string
}
export type TParticipacaoImobiliariaCorretor =
  | {
      imobiliariaCorretor: TImobiliariaCorretor
      corretagem: { tipo: 'percentual'; percentualCorretagemImobiliaria: number }
    }
  | { imobiliariaCorretor: TImobiliariaCorretor; corretagem: { tipo: 'valor'; valorCorretagemImobiliaria: number } }

export type TParticipacaoData =
  | {
      imobiliariaCorretorIdExterno: string
      percentualCorretagemImobiliaria: number
      formaPagamentoComissao: TFormaPagamentoComissaoEnum
    }
  | {
      imobiliariaCorretorIdExterno: string
      valorCorretagemImobiliaria: number
      formaPagamentoComissao: TFormaPagamentoComissaoEnum
    }

export type TPropostaData = {
  clienteAtendimentoId: string
  vendedorId: string
  compradores: Array<{
    pessoaId: string
    percentualParticipacao: number
    tipoCliente: TTipoDoCompradorEnum
    vinculos: Array<{
      clienteId: string
      tipo: TTipoDoVinculoDoCompradorEnum
      procuradoresIds: string[]
    }>
    procuradoresIds: string[]
    tipoAssinatura: TTipoAssinaturaEnum | null
  }>
  participacoes: TParticipacaoData[]
  assinaturaContrato: TAssinaturaContratoEnum
  observacoes?: string | null
  termosDaProposta?: string | null
  idExternoBloco: number
  opcaoDeVaga: TOpcaoVagaEnum
  idsExternosVagasPadrao: number[]
  plantaId: string | null
  simulacoes: Array<{ id: string; idExternoProduto: number; substituiVagaPadrao: boolean }>
  compradorTitularId: string
  tipoEmolumento: TTipoEmolumentoEnum | null
}

export type TPropostaRevisadaData = {
  numeroDaPropostaOrigem: number
  simulacoes: Array<{
    vendedorId: string
    clienteId: string
    idExternoBloco: number
    condicaoDePrecoId: string
    idExternoProduto: number
    tabelaDePrecoId: string
    entradas: TGrupoParcelas[]
    mensais: TGrupoParcelas[]
    intermediarias: Array<{
      periodicidade: 'Anual' | 'Semestral' | 'Trimestral' | 'Bimestral' | 'SemPeriodoFixo'
      parcelas: Array<{
        valor: number
        data: string
      }>
    }>
    intermediariasPosConclusao?: Array<{
      periodicidade: 'Anual' | 'Semestral' | 'Trimestral' | 'Bimestral' | 'SemPeriodoFixo'
      parcelas: Array<{
        valor: number
        data: string
      }>
    }> | null
    posConclusao: TGrupoParcelas[]
    saldo?: {
      data: string | null
      valor: number
    } | null
    tipoDoProduto: 'Unidade' | 'Acessorio' | 'Lote'
    rotinaArquivo: TRotinaArquivo
    valorTotalSimulado: number
    valorPoupanca: number
    valorSaldo?: number
    valorPosConclusao: number
    valorProposto?: number
    simulacaoAnteriorId: string
  }>
}

export type TCriarPropostaResponse = {
  id: string
  message: string
  date: string
  data: TPropostaData & { numero: number }
}

export type TGetPropostaResponse = {
  id: string
  numero: number
  data: string
  status: TStatusProposta
  titular: string
  nomeEmpreendimento: string
  nomeBloco: string
  unidade?: string
  lote?: string
  tipoDeEmpreendimento: 'Horizontal' | 'Vertical'
  descricaoPlanta?: string
  dataMarcacaoAlvenaria?: string
  acessorio?: string
  vagasPadrao?: string
  valorTotalProposta: number
  todosDocumentosEnviados: boolean
  numeroDaPropostaOrigem?: number
  propostasRevisadas?: number[]
  propostasDuplicadas?: number[]
  tipoPropostaOrigem: 'Revisao' | 'Duplicacao' | null
}

export const PapelCompradorPF = {
  COMPRADOR: 'Comprador',
  CONJUGE: 'Conjuge',
  MAE_PAI: 'MaePai',
  REPRESENTANTE: 'Representante',
  PROCURADOR: 'Procurador',
} as const

export type TPapelComprador = (typeof PapelCompradorPF)[keyof typeof PapelCompradorPF]

export type TPapelCompradorPF = Pick<typeof PapelCompradorPF, 'COMPRADOR' | 'CONJUGE' | 'MAE_PAI'>[keyof Pick<
  typeof PapelCompradorPF,
  'COMPRADOR' | 'CONJUGE' | 'MAE_PAI'
>]

export type TCompradorVinculoValido = 'Representante' | 'MaePai' | 'Conjuge'
type TCompradoresCadastradosVinculo = {
  nome: string
  maiorDe18Anos: boolean
  documento: string
  tipo: TCompradorVinculoValido
  id: string
}

export type TCompradoresCadastrados = Array<{
  documento: string
  id: string
  maiorDe18Anos: boolean | null
  nome: string
  tipo: 'PessoaFisica' | 'PessoaJuridica'
  vinculos: TCompradoresCadastradosVinculo[]
}>

export type TPessoaDocumento = {
  pessoaId: string
  nome: string
  tipoComprador: string
  documento: string
  quantidadeDocumentosAnexados: number
}

export type TGetListaCompradoresDocumentosResponse = TPessoaDocumento[]

export type TGetPropostaRevisarResponse = {
  numeroDaProposta: number
  simulacoes: TPropostaSimulacao[]
}

// Tipos para Salvar Duplicação de Proposta
export type TSimulacaoDuplicacaoData = {
  vendedorId: string
  clienteId: string
  idExternoBloco: number
  condicaoDePrecoId: string
  idExternoProduto: number
  tabelaDePrecoId: string
  entradas: Array<{
    parcelas: Array<{
      data: string
      valor: number
    }>
  }>
  mensais: Array<{
    parcelas: Array<{
      data: string
      valor: number
    }>
  }>
  intermediarias: Array<{
    parcelas: Array<{
      data: string
      valor: number
    }>
  }>
  posConclusao: Array<{
    parcelas: Array<{
      data: string
      valor: number
    }>
  }>
  intermediariasPosConclusao: Array<{
    parcelas: Array<{
      data: string
      valor: number
    }>
  }>
  saldo: {
    data: string
    valor: number
  } | null
  ato: {
    data: string
    valor: number
  } | null
  tipoDoProduto: TTipoProdutoValidoEnum
  rotinaArquivo: TRotinaArquivo
  valorTotalSimulado: number
  valorPoupanca: number
  valorSaldo: number
  valorPosConclusao: number
  valorProposto?: number
  valorItbi?: number
  custasCartorarias?: number
  valorMensalPrice?: number
  tipoDeAmortizacao: TTipoAmortizacaoEnum | null
  simulacaoAnteriorId: string
  valorIntermediariaPrice?: number
  formaDePagamento: TFormaDePagamentoEnum | null
}

export type TPropostaDuplicacaoData = {
  clienteAtendimentoId: string
  vendedorId: string
  compradores: Array<{
    pessoaId: string
    percentualParticipacao: number
    tipoCliente: TTipoDoCompradorEnum
    vinculos: Array<{
      clienteId: string
      tipo: TTipoDoVinculoDoCompradorEnum
      procuradoresIds: string[]
    }>
    procuradoresIds: string[]
    tipoAssinatura: TTipoAssinaturaEnum | null
  }>
  participacoes: Array<{
    imobiliariaCorretorIdExterno: string
    percentualCorretagemImobiliaria?: number | null
    valorCorretagemImobiliaria?: number | null
    formaPagamentoComissao: TFormaPagamentoComissaoEnum
  }>
  assinaturaContrato: TAssinaturaContratoEnum
  observacoes: string
  termosDaProposta: string
  idExternoBloco: number
  opcaoDeVaga: TOpcaoVagaEnum
  idsExternosVagasPadrao: number[]
  plantaId: string | null
  simulacoes: Array<{
    id: string
    idExternoProduto: number
    substituiVagaPadrao: boolean
  }>
  compradorTitularId: string
}

export type TSalvarDuplicacaoPropostaData = {
  numeroDaPropostaOrigem: number
  simulacoes: TSimulacaoDuplicacaoData[]
  proposta: TPropostaDuplicacaoData
}

export type TSalvarDuplicacaoPropostaResponse = {
  id: string
  message: string
  date: string
  data: TPropostaDuplicacaoData & { numero: number }
}
